import { DAWSON_CATEGORIES } from './dawsonCategories';

/**
 * Phase-Specific Photo Series Configurations
 *
 * Defines which Dawson photos are required/optional for each treatment phase.
 * This allows customization of the photo protocol based on the stage of treatment.
 */

export const PHASE_TYPES = {
  INITIAL: 'initial',
  POST_ORTHO: 'postOrtho',
  PROVISIONAL: 'provisional',
  FINAL: 'final'
};

export const PHASE_PHOTO_CONFIGS = {
  [PHASE_TYPES.INITIAL]: {
    phaseType: PHASE_TYPES.INITIAL,
    name: 'Initial Records',
    description: 'Complete diagnostic photo series for treatment planning',
    color: '#3B82F6', // Blue
    icon: 'DocumentPlusIcon',

    // All 21 photos for complete initial documentation
    photos: Object.keys(DAWSON_CATEGORIES),

    // Required photos for initial records
    requiredPhotos: [
      'frontal_smile',
      'frontal_occlusion',
      'right_buccal',
      'left_buccal',
      'maxillary_occlusal',
      'mandibular_occlusal',
      'anterior_retracted',
      'upper_anterior',
      'lower_anterior'
    ],

    notes: [
      'Capture all 21 photos for comprehensive baseline documentation',
      'Focus on diagnostic quality for treatment planning',
      'Include any areas of concern or pathology',
      'Ensure proper lighting and focus for all images'
    ]
  },

  [PHASE_TYPES.POST_ORTHO]: {
    phaseType: PHASE_TYPES.POST_ORTHO,
    name: 'Post-Orthodontic Records',
    description: 'Focus on occlusion changes after orthodontic treatment',
    color: '#10B981', // Green
    icon: 'CheckCircleIcon',

    // Subset focused on occlusion and alignment changes
    photos: [
      'frontal_smile',
      'frontal_occlusion',
      'right_buccal',
      'left_buccal',
      'maxillary_occlusal',
      'mandibular_occlusal',
      'anterior_retracted',
      'right_lateral',
      'left_lateral',
      'overjet',
      'overbite'
    ],

    // Required photos to document orthodontic results
    requiredPhotos: [
      'frontal_occlusion',
      'right_buccal',
      'left_buccal',
      'maxillary_occlusal',
      'mandibular_occlusal',
      'overjet',
      'overbite'
    ],

    notes: [
      'Document occlusal changes from orthodontic treatment',
      'Capture canine and molar relationships',
      'Show overjet and overbite corrections',
      'Include arch alignment and spacing closure'
    ]
  },

  [PHASE_TYPES.PROVISIONAL]: {
    phaseType: PHASE_TYPES.PROVISIONAL,
    name: 'Provisional Restoration Evaluation',
    description: 'Focus on esthetics and provisional crown/veneer placement',
    color: '#F59E0B', // Amber
    icon: 'SparklesIcon',

    // Subset focused on anterior esthetics
    photos: [
      'frontal_repose',
      'frontal_smile',
      'profile_smile',
      'three_quarter',
      'frontal_occlusion',
      'right_buccal',
      'left_buccal',
      'anterior_retracted',
      'upper_anterior',
      'lower_anterior',
      'palatal',
      'lingual'
    ],

    // Required photos to evaluate provisional restorations
    requiredPhotos: [
      'frontal_smile',
      'frontal_occlusion',
      'anterior_retracted',
      'upper_anterior'
    ],

    notes: [
      'Focus on esthetic evaluation of provisional restorations',
      'Capture lip support and smile line',
      'Document gingival health around provisionals',
      'Show emergence profile and contact areas'
    ]
  },

  [PHASE_TYPES.FINAL]: {
    phaseType: PHASE_TYPES.FINAL,
    name: 'Final Records',
    description: 'Complete documentation of finished treatment',
    color: '#8B5CF6', // Purple
    icon: 'TrophyIcon',

    // All 21 photos for complete final documentation
    photos: Object.keys(DAWSON_CATEGORIES),

    // Required photos for final records
    requiredPhotos: [
      'frontal_repose',
      'frontal_smile',
      'frontal_occlusion',
      'right_buccal',
      'left_buccal',
      'maxillary_occlusal',
      'mandibular_occlusal',
      'anterior_retracted',
      'upper_anterior',
      'lower_anterior'
    ],

    notes: [
      'Capture all 21 photos for complete final documentation',
      'Ensure highest quality for case presentation',
      'Document all esthetic and functional outcomes',
      'Include before/after comparison reference'
    ]
  }
};

/**
 * Get photo configuration for a specific phase
 * @param {string} phaseType - Phase type from PHASE_TYPES
 * @returns {Object} Phase configuration
 */
export const getPhaseConfig = (phaseType) => {
  return PHASE_PHOTO_CONFIGS[phaseType] || PHASE_PHOTO_CONFIGS[PHASE_TYPES.INITIAL];
};

/**
 * Get required photo categories for a phase
 * @param {string} phaseType - Phase type
 * @returns {Array} Array of required photo category IDs
 */
export const getRequiredPhotos = (phaseType) => {
  const config = getPhaseConfig(phaseType);
  return config.requiredPhotos || [];
};

/**
 * Get optional photo categories for a phase
 * @param {string} phaseType - Phase type
 * @returns {Array} Array of optional photo category IDs
 */
export const getOptionalPhotos = (phaseType) => {
  const config = getPhaseConfig(phaseType);
  const allPhotos = config.photos || [];
  const required = config.requiredPhotos || [];
  return allPhotos.filter(photoId => !required.includes(photoId));
};

/**
 * Get total photo count for a phase
 * @param {string} phaseType - Phase type
 * @returns {number} Total number of photos in phase
 */
export const getTotalPhotoCount = (phaseType) => {
  const config = getPhaseConfig(phaseType);
  return config.photos ? config.photos.length : 0;
};

/**
 * Get required photo count for a phase
 * @param {string} phaseType - Phase type
 * @returns {number} Number of required photos
 */
export const getRequiredPhotoCount = (phaseType) => {
  return getRequiredPhotos(phaseType).length;
};

/**
 * Check if a photo is required for a phase
 * @param {string} phaseType - Phase type
 * @param {string} photoId - Photo category ID
 * @returns {boolean} True if photo is required
 */
export const isPhotoRequired = (phaseType, photoId) => {
  return getRequiredPhotos(phaseType).includes(photoId);
};

/**
 * Check if phase is complete
 * @param {string} phaseType - Phase type
 * @param {Array} uploadedPhotoIds - Array of uploaded photo category IDs
 * @returns {Object} Completion status { isComplete, missingRequired, progress }
 */
export const getPhaseCompletionStatus = (phaseType, uploadedPhotoIds = []) => {
  const required = getRequiredPhotos(phaseType);
  const total = getTotalPhotoCount(phaseType);

  const uploaded = uploadedPhotoIds.length;
  const missingRequired = required.filter(id => !uploadedPhotoIds.includes(id));

  return {
    isComplete: missingRequired.length === 0,
    uploaded,
    total,
    required: required.length,
    missingRequired,
    progress: total > 0 ? Math.round((uploaded / total) * 100) : 0,
    requiredProgress: required.length > 0
      ? Math.round(((required.length - missingRequired.length) / required.length) * 100)
      : 0
  };
};

/**
 * Get all phase types in order
 * @returns {Array} Array of phase type strings
 */
export const getAllPhaseTypes = () => {
  return [
    PHASE_TYPES.INITIAL,
    PHASE_TYPES.POST_ORTHO,
    PHASE_TYPES.PROVISIONAL,
    PHASE_TYPES.FINAL
  ];
};

/**
 * Get phase display name
 * @param {string} phaseType - Phase type
 * @returns {string} Display name
 */
export const getPhaseName = (phaseType) => {
  const config = getPhaseConfig(phaseType);
  return config.name;
};

export default PHASE_PHOTO_CONFIGS;
